import { useMutation, useQuery } from '@apollo/client';
import { FileDropzoneInput, useArchiveFile, useDownloadFile, useUploadFiles } from '@newfront-insurance/admin-ui/files';
import { SegmentedControlV2 } from '@newfront-insurance/core-ui';
import { Button, ComboboxInput, Input } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { FileArchive, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildSkillZipFile,
  extractSkillBundleFromZipBuffer,
  fetchSkillBundleFromZipUrl,
  readBlobAsArrayBuffer,
  type ParsedSkillBundle,
  type SkillPackageEntry,
} from './custom-skill-zip';
import { SkillContentEditor } from './skill-content-editor';
import { normalizeSkillCommand, suggestSkillCommand } from '../../../lib/skill-command-utils';
import { GET_CUSTOM_SKILLS } from '../../../queries/chats';
import type { CustomSkillRecord, NovaSkill } from '../../../queries/chats';
import { BUILD_CUSTOM_SKILL, CREATE_CUSTOM_SKILL, UPDATE_CUSTOM_SKILL } from '../../../queries/mutations';
import { Suggestion, Suggestions } from '../../ai-elements';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useSkillsSafe } from '../contexts/skills-context';
import { getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';

export const ZIP_ACCEPT = '.zip,application/zip,application/x-zip-compressed';
const ALL_CUSTOM_SKILL_FILTERS = { enabled: [true, false] };
const fieldLabelClassName = "font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#262D46]";
const fieldCardClassName = 'rounded-[4px] border border-[#BEC7D0] bg-white';
const formFieldWrapperClassName = 'flex flex-col gap-2';
const inputClassName = "font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#262D46] placeholder:text-[#98A6B5]";
const createViewContentClassName = 'space-y-[25px] px-5 py-5';
const primaryFooterButtonClassName = "min-w-[112px] font-['Open_Sans',sans-serif] text-[14px] leading-5";
const secondaryFooterButtonClassName = "min-w-[112px] font-['Open_Sans',sans-serif] text-[14px] leading-5";
const titleClassName = "font-['Open_Sans',sans-serif] text-[17px] leading-6 font-normal text-[#262D46]";
const helperTextClassName = "font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]";
const errorTextClassName = "font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#C5342A]";
const slashPrefixClassName =
  "flex h-[38px] w-10 shrink-0 items-center justify-center border-r border-[#BEC7D0] bg-[#FAFBFC] font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#98A6B5]";
const slashCommandFieldClassName = 'flex overflow-hidden rounded-[4px] border border-[#BEC7D0] bg-white';
const segmentedControlOptionClassName =
  "!min-w-[100px] !rounded-full !border-transparent !px-8 !py-1 !font-['Open_Sans',sans-serif] " +
  '!text-[12px] !leading-4 !font-normal [&.isActive]:!border-transparent [&.isActive]:!bg-[#F4F5F6] ' +
  '[&.isActive]:!shadow-none';
const createSkillCommandRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const CREATE_WITH_NOVA_COMMAND = '/skill-creator';
const GENERATE_AI_SUMMARIES = true;
const customSkillDropzoneClassName =
  'flex min-h-[154px] items-center justify-center rounded-[4px] border border-dashed border-[#BEC7D0] bg-white px-6 text-center transition-colors hover:border-[#257FFB]';
const createWithNovaPromptPlaceholder =
  'Compare Workers Comp quotes side-by-side. Pull out premiums, deductibles, coverage limits, and claims reputation for each carrier, then recommend the best fit and explain why.';

export type CreateSkillMode = 'nova' | 'create' | 'import';

export interface UploadedCustomSkillBundle {
  fileUuid: string;
  name: string;
}

interface ImportedCustomSkillBundle {
  rawFile: File;
  name: string;
  bundle: ParsedSkillBundle;
}

export interface CreateSkillDraft {
  command: string;
  description: string;
  category: string;
  instructions: string;
}

type CreateSkillField = 'command' | 'description' | 'instructions';

interface CreateSkillValidationErrors {
  command?: string;
  description?: string;
  instructions?: string;
}

interface UpdateCustomSkillCommand {
  fileUuid?: string;
  frontmatter?: {
    name?: string;
    description?: string;
    category?: string;
  };
  content?: string;
}

interface BuildExistingSkillCommandsOptions {
  runtimeSkills?: Array<Pick<NovaSkill, 'id' | 'source' | 'command'>>;
  customSkills?: Array<Pick<CustomSkillRecord, 'uuid' | 'command'>>;
  skillToUpdate?: Pick<CustomSkillRecord, 'uuid' | 'command'> | null;
}

export const EMPTY_CREATE_SKILL_DRAFT: CreateSkillDraft = {
  command: '',
  description: '',
  category: '',
  instructions: '',
};

export function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function getCategoryLabel(skill: CustomSkillRecord) {
  return skill.frontmatter.category?.trim() || 'Uncategorized';
}

export function isZipFile(file: File) {
  return file.name.toLowerCase().endsWith('.zip');
}

function getSkillCommandFromFileName(fileName: string) {
  return suggestSkillCommand(fileName.replace(/\.zip$/i, ''));
}

function importedBundleToDraft(importedBundle: ImportedCustomSkillBundle): CreateSkillDraft {
  const command =
    getSkillCommandFromFileName(importedBundle.name) || suggestSkillCommand(importedBundle.bundle.frontmatter.name);

  return {
    command,
    description: importedBundle.bundle.frontmatter.description ?? '',
    category: importedBundle.bundle.frontmatter.category ?? '',
    instructions: importedBundle.bundle.content,
  };
}

async function parseImportedSkillBundle(file: File): Promise<ImportedCustomSkillBundle> {
  if (!isZipFile(file)) {
    throw new Error('Upload a .zip bundle to create a custom skill.');
  }

  return {
    rawFile: file,
    name: file.name,
    bundle: await extractSkillBundleFromZipBuffer(await readBlobAsArrayBuffer(file)),
  };
}

function buildImportedSkillZipFile(importedBundle: ImportedCustomSkillBundle, draft: CreateSkillDraft) {
  const category = draft.category.trim();

  return buildSkillZipFile({
    sourceFile: importedBundle.rawFile,
    skillFilePath: importedBundle.bundle.skillFilePath,
    frontmatter: {
      name: suggestSkillCommand(draft.command),
      description: draft.description.trim(),
      ...(category ? { category } : {}),
    },
    content: draft.instructions,
  });
}

function collectCategories(values: Array<string | null | undefined>) {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])].sort((left, right) =>
    left.localeCompare(right),
  );
}

export function buildSuggestedSkillCategories(
  runtimeSkills: Array<Pick<NovaSkill, 'category'>> = [],
  customSkills: Array<Pick<CustomSkillRecord, 'frontmatter'>> = [],
) {
  return collectCategories([
    ...runtimeSkills.map((skill) => skill.category),
    ...customSkills.map((skill) => skill.frontmatter.category),
  ]);
}

export function isCreateSkillDraftValid(draft: CreateSkillDraft) {
  return Boolean(draft.command.trim() && draft.description.trim() && draft.instructions.trim());
}

export function buildCreateWithNovaMessage(prompt: string) {
  const trimmedPrompt = prompt.trim();
  return trimmedPrompt ? `${CREATE_WITH_NOVA_COMMAND} ${trimmedPrompt}` : '';
}

export function validateCreateSkillDraft(
  draft: CreateSkillDraft,
  existingCommands: string[] = [],
  options: { allowEmptyInstructions?: boolean } = {},
): CreateSkillValidationErrors {
  const errors: CreateSkillValidationErrors = {};
  const rawCommand = draft.command.trim();
  const suggestedCommand = suggestSkillCommand(rawCommand);

  if (!rawCommand) {
    errors.command = 'Enter a slash command name.';
  } else if (existingCommands.some((command) => normalizeSkillCommand(command) === suggestedCommand)) {
    errors.command = 'That slash command already exists.';
  } else if (rawCommand !== suggestedCommand || !createSkillCommandRegex.test(suggestedCommand)) {
    errors.command = 'Use lowercase letters, numbers, and single hyphens only.';
  }

  if (!draft.description.trim()) {
    errors.description = 'Describe when Nova should use this skill.';
  }

  if (!options.allowEmptyInstructions && !draft.instructions.trim()) {
    errors.instructions = 'Enter the skill content.';
  }

  return errors;
}

export function customSkillToDraft(skill: CustomSkillRecord, instructions = ''): CreateSkillDraft {
  return {
    command: skill.command,
    description: skill.frontmatter.description,
    category: skill.frontmatter.category ?? '',
    instructions,
  };
}

function getUpdateFrontmatter(originalDraft: CreateSkillDraft, draft: CreateSkillDraft) {
  const frontmatter: NonNullable<UpdateCustomSkillCommand['frontmatter']> = {};
  const suggestedCommand = suggestSkillCommand(draft.command);

  if (suggestedCommand && suggestedCommand !== normalizeSkillCommand(originalDraft.command)) {
    frontmatter.name = suggestedCommand;
  }

  const nextDescription = draft.description.trim();
  if (nextDescription !== originalDraft.description.trim()) {
    frontmatter.description = nextDescription;
  }

  const nextCategory = draft.category.trim();
  if (nextCategory && nextCategory !== originalDraft.category.trim()) {
    frontmatter.category = nextCategory;
  }

  return Object.keys(frontmatter).length > 0 ? frontmatter : undefined;
}

function isCurrentRuntimeCustomSkill(
  skill: Pick<NovaSkill, 'id' | 'source'>,
  skillToUpdate?: Pick<CustomSkillRecord, 'uuid'> | null,
) {
  return Boolean(skillToUpdate?.uuid) && skill.source === 'personal' && skill.id === skillToUpdate?.uuid;
}

export function buildExistingSkillCommands({
  runtimeSkills = [],
  customSkills = [],
  skillToUpdate,
}: BuildExistingSkillCommandsOptions) {
  const currentCommand = normalizeSkillCommand(skillToUpdate?.command ?? '');
  const commands = [
    ...runtimeSkills
      .filter((skill) => !isCurrentRuntimeCustomSkill(skill, skillToUpdate))
      .map((skill) => skill.command),
    ...customSkills.filter((skill) => skill.uuid !== skillToUpdate?.uuid).map((skill) => skill.command),
  ];

  return commands.filter((command) => normalizeSkillCommand(command) !== currentCommand);
}

export function buildUpdateCustomSkillCommand({
  originalDraft,
  draft,
  contentTouched,
  fileUuid,
}: {
  originalDraft: CreateSkillDraft;
  draft: CreateSkillDraft;
  contentTouched: boolean;
  fileUuid?: string;
}): UpdateCustomSkillCommand {
  const command: UpdateCustomSkillCommand = {};
  const frontmatter = getUpdateFrontmatter(originalDraft, draft);

  if (fileUuid) {
    command.fileUuid = fileUuid;
  }

  if (frontmatter) {
    command.frontmatter = frontmatter;
  }

  if (contentTouched) {
    command.content = draft.instructions;
  }

  return command;
}

export function useCustomSkillBundleUpload() {
  const notify = useNotify();
  const { upload } = useUploadFiles();
  const { mutateAsync: archiveFile } = useArchiveFile({});

  const archiveUploadedBundle = useCallback(
    async (bundle: UploadedCustomSkillBundle | null) => {
      if (!bundle?.fileUuid) {
        return;
      }

      try {
        await archiveFile({ fileUuid: bundle.fileUuid });
      } catch {
        // Best-effort cleanup for abandoned uploaded bundles.
      }
    },
    [archiveFile],
  );

  const uploadBundle = useCallback(
    async (file: File) => {
      if (!isZipFile(file)) {
        throw new Error('Upload a .zip bundle to create a custom skill.');
      }

      const uploadedFiles = await upload({
        files: [
          {
            rawFile: file,
            category: 'AI_CHAT_ATTACHMENT',
          },
        ],
      });

      const uploadedFileUuid = uploadedFiles[0]?.id;
      if (!uploadedFileUuid) {
        throw new Error('Upload succeeded without a file UUID.');
      }

      return {
        fileUuid: uploadedFileUuid,
        name: file.name,
      } satisfies UploadedCustomSkillBundle;
    },
    [upload],
  );

  return {
    uploadBundle,
    archiveUploadedBundle,
    notifyUploadError: (error: unknown, fallback: string) => {
      notify.error(getErrorMessage(error, fallback));
    },
  };
}

function ImportedSkillBundleSummary({
  importedBundle,
  disabled,
  onRemove,
}: {
  importedBundle: ImportedCustomSkillBundle;
  disabled?: boolean;
  onRemove: () => void;
}) {
  return (
    <div className={`${fieldCardClassName} flex items-center justify-between gap-4 px-4 py-4`}>
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#F4F8FE] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]">
          <FileArchive className="h-4 w-4 text-[#546A83]" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm text-[#262D46]">{importedBundle.name}</p>
          <p className="mt-1 text-xs text-[#546A83]">Review the package below before saving it to Nova.</p>
        </div>
      </div>

      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-[#546A83] transition-colors hover:text-[#262D46] disabled:opacity-50"
        aria-label="Remove imported ZIP bundle"
      >
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  );
}

function PackageContentsPreview({ entries }: { entries: SkillPackageEntry[] }) {
  return (
    <div className={`${fieldCardClassName} overflow-hidden`}>
      <div className="flex items-center justify-between border-b border-[#DEE2E7] px-4 py-3">
        <h3 className="font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#262D46]">
          Package contents
        </h3>
        <span className="font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
          {entries.length} {entries.length === 1 ? 'file' : 'files'}
        </span>
      </div>
      <div>
        {entries.map((entry) => (
          <div
            key={entry.path}
            className={`flex min-h-[50px] items-center justify-between gap-4 border-b border-[#DEE2E7] px-4 py-3 last:border-b-0 ${
              entry.isPrimarySkillReference ? 'bg-[#F4F8FE]' : 'bg-white'
            }`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="inline-flex h-5 min-w-8 items-center justify-center rounded-[3px] bg-[#546A83] px-1.5 font-mono text-[10px] leading-3 font-semibold text-white">
                {entry.extension}
              </span>
              <span className="truncate text-sm text-[#262D46]" title={entry.path}>
                {entry.name}
              </span>
            </div>
            <span className="shrink-0 text-right text-xs text-[#546A83]">{entry.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CreateSkillView({
  onBack,
  initialMode = 'nova',
  skillToUpdate,
}: {
  onBack: () => void;
  initialMode?: CreateSkillMode;
  skillToUpdate?: CustomSkillRecord;
}) {
  const notify = useNotify();
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const skillsContext = useSkillsSafe();
  const { uploadBundle, archiveUploadedBundle, notifyUploadError } = useCustomSkillBundleUpload();
  const { previewUrl } = useDownloadFile({});
  const [buildCustomSkill] = useMutation(BUILD_CUSTOM_SKILL);
  const [createCustomSkill] = useMutation(CREATE_CUSTOM_SKILL);
  const [updateCustomSkill] = useMutation(UPDATE_CUSTOM_SKILL);
  const isUpdate = Boolean(skillToUpdate);
  const canCreateWithNova = Boolean(chatOverlayContext?.startNewChatWithInput) && !isUpdate;
  const initialModeForView: CreateSkillMode =
    (isUpdate || !canCreateWithNova) && initialMode === 'nova' ? 'create' : initialMode;
  const initialDraft = useMemo(
    () => (skillToUpdate ? customSkillToDraft(skillToUpdate) : EMPTY_CREATE_SKILL_DRAFT),
    [skillToUpdate],
  );
  const previewUrlRef = useRef(previewUrl);
  const importedBundleRef = useRef<ImportedCustomSkillBundle | null>(null);
  const [mode, setMode] = useState<CreateSkillMode>(initialModeForView);
  const [draft, setDraft] = useState(initialDraft);
  const [originalDraft, setOriginalDraft] = useState(initialDraft);
  const [novaPrompt, setNovaPrompt] = useState('');
  const [importedBundle, setImportedBundle] = useState<ImportedCustomSkillBundle | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingExistingContent, setIsLoadingExistingContent] = useState(false);
  const [isProcessingImportFile, setIsProcessingImportFile] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<CreateSkillField, boolean>>({
    command: false,
    description: false,
    instructions: false,
  });
  const [isContentEdited, setIsContentEdited] = useState(false);
  const isInstructionsTouchedRef = useRef(false);
  const notifyRef = useRef(notify);
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const [showNovaPromptValidation, setShowNovaPromptValidation] = useState(false);
  const { data: customSkillsData, refetch: refetchCustomSkills } = useQuery(GET_CUSTOM_SKILLS, {
    variables: { filters: ALL_CUSTOM_SKILL_FILTERS },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const categorySuggestions = useMemo(
    () => buildSuggestedSkillCategories(skillsContext?.skills ?? [], customSkillsData?.customSkills ?? []),
    [customSkillsData?.customSkills, skillsContext?.skills],
  );
  const existingSkillCommands = useMemo(
    () =>
      buildExistingSkillCommands({
        runtimeSkills: skillsContext?.skills,
        customSkills: customSkillsData?.customSkills,
        skillToUpdate,
      }),
    [customSkillsData?.customSkills, skillToUpdate, skillsContext?.skills],
  );
  const createValidationErrors = useMemo(
    () => validateCreateSkillDraft(draft, existingSkillCommands, { allowEmptyInstructions: isUpdate }),
    [draft, existingSkillCommands, isUpdate],
  );
  const suggestedCommand = useMemo(() => suggestSkillCommand(draft.command), [draft.command]);
  const shouldShowCommandSuggestion = Boolean(
    draft.command.trim() &&
      suggestedCommand &&
      draft.command.trim() !== suggestedCommand &&
      (showCreateValidation || touchedFields.command) &&
      createValidationErrors.command,
  );
  const hasCreateValidationErrors = Object.keys(createValidationErrors).length > 0;
  const descriptionCount = draft.description.length;
  const createWithNovaMessage = useMemo(() => buildCreateWithNovaMessage(novaPrompt), [novaPrompt]);
  const createModeOptions = useMemo<Array<{ label: string; value: CreateSkillMode }>>(() => {
    if (isUpdate) {
      return [
        { label: 'Edit skill', value: 'create' },
        { label: 'Import file', value: 'import' },
      ];
    }

    const options: Array<{ label: string; value: CreateSkillMode }> = [];
    if (canCreateWithNova) {
      options.push({ label: 'Create with Nova', value: 'nova' });
    }
    options.push({ label: 'Write it myself', value: 'create' }, { label: 'Import file', value: 'import' });
    return options;
  }, [canCreateWithNova, isUpdate]);
  const updateCommand = useMemo(
    () =>
      buildUpdateCustomSkillCommand({
        originalDraft,
        draft,
        contentTouched: isContentEdited,
      }),
    [draft, isContentEdited, originalDraft],
  );
  const hasUpdateChanges = Boolean(importedBundle) || Object.keys(updateCommand).length > 0;
  const isCreateDisabled =
    mode === 'nova'
      ? !createWithNovaMessage || isCreating
      : isUpdate && mode === 'create'
        ? hasCreateValidationErrors ||
          isCreating ||
          isLoadingExistingContent ||
          isProcessingImportFile ||
          !hasUpdateChanges
        : mode === 'import'
          ? !importedBundle || isCreating || isProcessingImportFile
          : hasCreateValidationErrors || isCreating || isProcessingImportFile;

  useEffect(() => {
    importedBundleRef.current = importedBundle;
  }, [importedBundle]);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  const skillToUpdateSourceFileUuid = skillToUpdate?.sourceFileUuid;
  const skillToUpdateUuid = skillToUpdate?.uuid;

  useEffect(() => {
    setMode(initialModeForView);
    setDraft(initialDraft);
    setOriginalDraft(initialDraft);
    setNovaPrompt('');
    setTouchedFields({
      command: false,
      description: false,
      instructions: false,
    });
    setIsContentEdited(false);
    setShowCreateValidation(false);
    setShowNovaPromptValidation(false);
    setImportedBundle(null);
    importedBundleRef.current = null;
    isInstructionsTouchedRef.current = false;
  }, [initialDraft, initialModeForView]);

  useEffect(() => {
    if (!skillToUpdateSourceFileUuid) {
      return;
    }

    const sourceFileUuid = skillToUpdateSourceFileUuid;
    let isCancelled = false;
    setIsLoadingExistingContent(true);

    async function loadExistingSkillContent() {
      const contentUrl = await previewUrlRef.current(sourceFileUuid);

      if (!contentUrl) {
        throw new Error('Unable to resolve the custom skill ZIP URL.');
      }

      return fetchSkillBundleFromZipUrl(contentUrl);
    }

    void loadExistingSkillContent()
      .then((bundle) => {
        if (isCancelled) {
          return;
        }

        const instructions = bundle.content;
        setOriginalDraft((currentDraft) => ({ ...currentDraft, instructions }));

        if (!isInstructionsTouchedRef.current) {
          setDraft((currentDraft) => ({ ...currentDraft, instructions }));
        }
      })
      .catch((loadError) => {
        if (!isCancelled) {
          notifyRef.current.error(getErrorMessage(loadError, 'Unable to load skill content from the existing ZIP.'));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingExistingContent(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [skillToUpdateSourceFileUuid, skillToUpdateUuid]);

  const clearImportedBundle = useCallback(() => {
    if (!importedBundleRef.current) {
      return;
    }

    importedBundleRef.current = null;
    setImportedBundle(null);
  }, []);

  const handleBack = useCallback(async () => {
    clearImportedBundle();
    onBack();
  }, [clearImportedBundle, onBack]);

  const handleImportDrop = useCallback(
    async (files: Array<{ rawFile: File; category?: string }>) => {
      const nextFile = files[0]?.rawFile;

      if (!nextFile) {
        return;
      }

      setIsProcessingImportFile(true);

      try {
        const nextBundle = await parseImportedSkillBundle(nextFile);
        const nextDraft = importedBundleToDraft(nextBundle);
        importedBundleRef.current = nextBundle;
        setImportedBundle(nextBundle);
        setDraft(nextDraft);
        setTouchedFields({
          command: false,
          description: false,
          instructions: false,
        });
        setIsContentEdited(false);
        setShowCreateValidation(false);
        isInstructionsTouchedRef.current = false;
        setMode('create');
      } catch (uploadError) {
        notifyUploadError(uploadError, 'Unable to read custom skill ZIP.');
      } finally {
        setIsProcessingImportFile(false);
      }
    },
    [notifyUploadError],
  );

  const uploadImportedBundle = useCallback(async () => {
    if (!importedBundle) {
      return null;
    }

    const skillZipFile = await buildImportedSkillZipFile(importedBundle, draft);
    return uploadBundle(skillZipFile);
  }, [draft, importedBundle, uploadBundle]);

  const handleCreateWithNova = useCallback(() => {
    const messageText = buildCreateWithNovaMessage(novaPrompt);

    if (!messageText) {
      setShowNovaPromptValidation(true);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_VALIDATION_FAILED, {
        chatUuid: chatOverlayContext?.chatUuid,
        creationMode: 'nova',
        errorFields: ['prompt'],
      });
      return;
    }

    if (!chatOverlayContext?.startNewChatWithInput) {
      notify.error('Unable to start a skill creator chat from here.');
      return;
    }

    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_STARTED, {
      chatUuid: chatOverlayContext.chatUuid,
      creationMode: 'nova',
      instructionLengthBucket: getLengthBucket(novaPrompt.trim().length),
      skillSource: 'nova',
    });

    chatOverlayContext.startNewChatWithInput(messageText);
    chatOverlayContext.setIsSettingsOpen(false);
    onBack();
  }, [analytics, chatOverlayContext, notify, novaPrompt, onBack]);

  const handleCreate = useCallback(async () => {
    const effectiveCreationMode: Exclude<CreateSkillMode, 'nova'> = importedBundle
      ? 'import'
      : mode === 'import'
        ? 'import'
        : 'create';
    const skillSource = importedBundle ? 'zip' : mode === 'import' ? 'zip' : 'manual';

    if ((mode === 'create' || importedBundle) && hasCreateValidationErrors) {
      setShowCreateValidation(true);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_VALIDATION_FAILED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle: suggestedCommand,
        creationMode: effectiveCreationMode,
        errorFields: Object.keys(createValidationErrors),
      });
      return;
    }

    if (isUpdate) {
      if (!skillToUpdate) {
        return;
      }

      if (mode === 'import' && !importedBundle) {
        notify.error('Choose a .zip bundle to update this custom skill.');
        return;
      }

      setIsCreating(true);
      let uploadedBundleForRollback: UploadedCustomSkillBundle | null = null;
      try {
        uploadedBundleForRollback = await uploadImportedBundle();
        const { data } = await updateCustomSkill({
          variables: {
            uuid: skillToUpdate.uuid,
            command: uploadedBundleForRollback?.fileUuid
              ? {
                  fileUuid: uploadedBundleForRollback.fileUuid,
                }
              : updateCommand,
          },
        });

        if (!data?.updateCustomSkill) {
          throw new Error('Missing updated custom skill.');
        }

        await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills({ filters: ALL_CUSTOM_SKILL_FILTERS })]);
        importedBundleRef.current = null;
        setImportedBundle(null);
        notify.success('Custom skill updated.');
        onBack();
      } catch (updateError) {
        if (uploadedBundleForRollback) {
          await archiveUploadedBundle(uploadedBundleForRollback);
        }
        notify.error(getErrorMessage(updateError, 'Unable to update custom skill.'));
      } finally {
        setIsCreating(false);
      }
      return;
    }

    setIsCreating(true);
    const usesDraftContent = mode === 'create' || Boolean(importedBundle);
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_STARTED, {
      category: usesDraftContent ? draft.category.trim() || undefined : undefined,
      chatUuid: chatOverlayContext?.chatUuid,
      commandHandle: usesDraftContent ? suggestedCommand : undefined,
      creationMode: effectiveCreationMode,
      descriptionLengthBucket: usesDraftContent ? getLengthBucket(draft.description.trim().length) : undefined,
      hasCategory: usesDraftContent ? Boolean(draft.category.trim()) : undefined,
      instructionLengthBucket: usesDraftContent ? getLengthBucket(draft.instructions.trim().length) : undefined,
      skillSource,
    });
    let uploadedBundleForRollback: UploadedCustomSkillBundle | null = null;
    try {
      let createdSkill: CustomSkillRecord | undefined;

      if (mode === 'import' || importedBundle) {
        if (!importedBundle) {
          notify.error('Choose a .zip bundle to create a custom skill.');
          return;
        }

        uploadedBundleForRollback = await uploadImportedBundle();
        if (!uploadedBundleForRollback?.fileUuid) {
          throw new Error('Unable to upload custom skill ZIP.');
        }

        const { data } = await createCustomSkill({
          variables: {
            command: {
              fileUuid: uploadedBundleForRollback.fileUuid,
              enabled: true,
              generateAiSummaries: GENERATE_AI_SUMMARIES,
            },
          },
        });
        createdSkill = data?.createCustomSkill;
      } else {
        const { data } = await buildCustomSkill({
          variables: {
            command: {
              frontmatter: {
                name: suggestSkillCommand(draft.command),
                description: draft.description.trim(),
                category: draft.category.trim() || null,
              },
              content: draft.instructions,
              enabled: true,
              generateAiSummaries: GENERATE_AI_SUMMARIES,
            },
          },
        });

        if (!data?.buildCustomSkill) {
          throw new Error('Missing created custom skill.');
        }
        createdSkill = data.buildCustomSkill;
      }

      await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills({ filters: ALL_CUSTOM_SKILL_FILTERS })]);
      importedBundleRef.current = null;
      setImportedBundle(null);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_SUCCEEDED, {
        category: createdSkill?.frontmatter.category,
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle: createdSkill?.command,
        creationMode: effectiveCreationMode,
        skillId: createdSkill?.uuid,
        skillSource: createdSkill?.source,
      });
      notify.success('Custom skill created.');
      onBack();
    } catch (createError) {
      if (uploadedBundleForRollback) {
        await archiveUploadedBundle(uploadedBundleForRollback);
      }
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_FAILED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle: usesDraftContent ? suggestedCommand : undefined,
        creationMode: effectiveCreationMode,
        errorName: createError instanceof Error ? createError.name : undefined,
        skillSource,
      });
      notify.error(getErrorMessage(createError, 'Unable to create custom skill.'));
    } finally {
      setIsCreating(false);
    }
  }, [
    archiveUploadedBundle,
    buildCustomSkill,
    analytics,
    chatOverlayContext?.chatUuid,
    createCustomSkill,
    createValidationErrors,
    draft,
    hasCreateValidationErrors,
    importedBundle,
    isUpdate,
    mode,
    notify,
    onBack,
    refetchCustomSkills,
    skillToUpdate,
    skillsContext,
    suggestedCommand,
    updateCommand,
    updateCustomSkill,
    uploadImportedBundle,
  ]);

  useEffect(() => {
    return () => {
      if (importedBundleRef.current) {
        importedBundleRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={createViewContentClassName}>
          <h2 className={titleClassName}>{isUpdate ? 'Edit Skills' : 'Add new skill'}</h2>

          <SegmentedControlV2
            value={mode}
            onChange={(value) => {
              const nextMode = value as CreateSkillMode;
              if (nextMode !== 'create' && importedBundleRef.current) {
                clearImportedBundle();
              }
              if (nextMode === 'nova') {
                setShowNovaPromptValidation(false);
              }
              setMode(nextMode);
            }}
            options={createModeOptions}
            activeColor="default"
            inactiveColor="#546A83"
            fontWeight={400}
            inactiveFontWeight={400}
            containerClassname="!inline-flex !rounded-full !border-[#DEE2E7] !bg-white !p-1"
            optionClassname={segmentedControlOptionClassName}
          />

          {mode === 'nova' ? (
            <div className={formFieldWrapperClassName}>
              <label htmlFor="custom-skill-nova-prompt" className={fieldLabelClassName}>
                What should this skill do?*
              </label>
              <p className={helperTextClassName}>
                Describe the skill in plain language: what it does, what it returns, and any specific behavior you want.
                Do not worry about getting it perfect; Nova will polish it and ask follow-ups if needed.
              </p>
              <textarea
                id="custom-skill-nova-prompt"
                aria-label="What should this skill do?"
                value={novaPrompt}
                onChange={(event) => {
                  setNovaPrompt(event.target.value);
                  if (showNovaPromptValidation && event.target.value.trim()) {
                    setShowNovaPromptValidation(false);
                  }
                }}
                placeholder={createWithNovaPromptPlaceholder}
                disabled={isCreating}
                className={`${fieldCardClassName} ${inputClassName} min-h-[154px] w-full resize-none px-[14px] py-3 transition-colors outline-none focus:border-[#257FFB] ${
                  showNovaPromptValidation && !novaPrompt.trim() ? 'border-[#C5342A]' : ''
                } disabled:cursor-not-allowed disabled:opacity-50`}
              />
              {showNovaPromptValidation && !novaPrompt.trim() ? (
                <p className={errorTextClassName}>Describe what the skill should do.</p>
              ) : null}
            </div>
          ) : mode === 'import' ? (
            <div className="space-y-4">
              <FileDropzoneInput
                multiple={false}
                accept={ZIP_ACCEPT}
                category="AI_CHAT_ATTACHMENT"
                loading={isProcessingImportFile}
                onDrop={(files) => void handleImportDrop(files)}
                customDropzoneComponent={
                  <div className={customSkillDropzoneClassName}>
                    {isProcessingImportFile ? (
                      <div className="inline-flex items-center gap-3 text-[14px] leading-5 text-[#546A83]">
                        <Loader2 className="h-5 w-5 animate-spin text-[#257FFB]" />
                        <span>Reading skill file...</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-4 text-[14px] leading-5 text-[#257FFB]">
                        <Plus className="h-6 w-6 shrink-0" strokeWidth={2.5} />
                        <span>Drop a skill file here or choose a file</span>
                      </div>
                    )}
                  </div>
                }
              />

              {importedBundle ? (
                <div className={`${fieldCardClassName} flex items-center justify-between gap-4 px-4 py-4`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#F4F8FE] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]">
                      <FileArchive className="h-4 w-4 text-[#546A83]" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-[#262D46]">{importedBundle.name}</p>
                      <p className="mt-1 text-xs text-[#546A83]">ZIP bundle parsed. Review it before saving.</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={clearImportedBundle}
                    disabled={isCreating || isProcessingImportFile}
                    className="text-[#546A83] transition-colors hover:text-[#262D46] disabled:opacity-50"
                    aria-label="Remove imported ZIP bundle"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              {importedBundle ? (
                <>
                  <ImportedSkillBundleSummary
                    importedBundle={importedBundle}
                    disabled={isCreating || isProcessingImportFile}
                    onRemove={clearImportedBundle}
                  />
                  <PackageContentsPreview entries={importedBundle.bundle.entries} />
                </>
              ) : null}

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-command" className={fieldLabelClassName}>
                  Slash command name*
                </label>
                <p className={helperTextClassName}>The command you will type to invoke this skill.</p>
                <div
                  className={`${slashCommandFieldClassName} ${
                    (showCreateValidation || touchedFields.command) && createValidationErrors.command
                      ? 'border-[#C5342A]'
                      : ''
                  }`}
                >
                  <div
                    className={`${slashPrefixClassName} ${
                      (showCreateValidation || touchedFields.command) && createValidationErrors.command
                        ? 'border-[#C5342A]'
                        : ''
                    }`}
                  >
                    /
                  </div>
                  <input
                    id="custom-skill-command"
                    aria-label="Slash command name"
                    type="text"
                    placeholder="workers-comp-compare"
                    value={draft.command}
                    onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, command: event.target.value }))}
                    onBlur={() => setTouchedFields((current) => ({ ...current, command: true }))}
                    disabled={isCreating}
                    className={`${inputClassName} h-[38px] w-full border-0 bg-transparent px-6 outline-none disabled:cursor-not-allowed disabled:opacity-50`}
                  />
                </div>
                {shouldShowCommandSuggestion ? (
                  <Suggestions>
                    <Suggestion
                      suggestion={suggestedCommand}
                      className="h-auto border border-[#D9E6FF] bg-[#F4F8FE] px-3 py-1.5 text-[12px] leading-4 font-semibold text-[#257FFB] hover:bg-[#E8F1FE]"
                      onClick={(nextCommand) => {
                        setDraft((currentDraft) => ({ ...currentDraft, command: nextCommand }));
                        setTouchedFields((current) => ({ ...current, command: true }));
                      }}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span>Use</span>
                        <span className="font-mono">/{suggestedCommand}</span>
                      </span>
                    </Suggestion>
                  </Suggestions>
                ) : null}
                {(showCreateValidation || touchedFields.command) && createValidationErrors.command ? (
                  <p className={errorTextClassName}>{createValidationErrors.command}</p>
                ) : null}
              </div>

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-description" className={fieldLabelClassName}>
                  When should Nova use this skill?*
                </label>
                <p className={helperTextClassName}>
                  Describe the signals or user requests that should trigger this skill automatically.
                </p>
                <Input
                  type="text"
                  fieldId="custom-skill-description"
                  aria-label="When should Nova use this skill?"
                  value={draft.description}
                  onChange={(event) =>
                    setDraft((currentDraft) => ({
                      ...currentDraft,
                      description: event.target.value.slice(0, 88),
                    }))
                  }
                  onBlur={() => setTouchedFields((current) => ({ ...current, description: true }))}
                  placeholder="Use when I share multiple Workers Comp quotes or asks which carrier to choose."
                  maxLength={88}
                  disabled={isCreating}
                  wrapperClassName={`bg-white ${
                    (showCreateValidation || touchedFields.description) && createValidationErrors.description
                      ? 'border-[#C5342A]'
                      : 'border-[#BEC7D0]'
                  }`}
                  inputClassName={inputClassName}
                />
                {(showCreateValidation || touchedFields.description) && createValidationErrors.description ? (
                  <p className={errorTextClassName}>{createValidationErrors.description}</p>
                ) : null}
                <p className={helperTextClassName}>{descriptionCount}/88 characters</p>
              </div>

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-category" className={fieldLabelClassName}>
                  Category
                </label>
                <ComboboxInput
                  fieldId="custom-skill-category"
                  aria-label="Category"
                  value={draft.category}
                  onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, category: event.target.value }))}
                  suggestions={categorySuggestions}
                  emptyText="Type to create a new category"
                  maxSuggestions={8}
                  minChars={0}
                  showOnFocus
                  placeholder="e.g. renewals"
                  disabled={isCreating}
                  wrapperClassName="border-[#BEC7D0] bg-white"
                  inputClassName={inputClassName}
                />
                {categorySuggestions.length > 0 ? (
                  <p className={helperTextClassName}>Choose an existing category or type a new one.</p>
                ) : null}
              </div>

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-content" className={fieldLabelClassName}>
                  Skill content*
                </label>
                <SkillContentEditor
                  id="custom-skill-content"
                  value={draft.instructions}
                  onChange={(instructions) => {
                    isInstructionsTouchedRef.current = true;
                    setIsContentEdited(true);
                    setTouchedFields((current) => ({ ...current, instructions: true }));
                    setDraft((currentDraft) => ({ ...currentDraft, instructions }));
                  }}
                  onBlur={() => setTouchedFields((current) => ({ ...current, instructions: true }))}
                  className={
                    (showCreateValidation || touchedFields.instructions) && createValidationErrors.instructions
                      ? 'border-[#C5342A]'
                      : ''
                  }
                  disabled={isCreating || isLoadingExistingContent}
                />
                {(showCreateValidation || touchedFields.instructions) && createValidationErrors.instructions ? (
                  <p className={errorTextClassName}>{createValidationErrors.instructions}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-[13px] border-t border-[#DEE2E7] bg-white px-5 py-4">
        <Button
          type="button"
          variant="secondary"
          className={secondaryFooterButtonClassName}
          onClick={() => void handleBack()}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className={primaryFooterButtonClassName}
          disabled={isCreateDisabled}
          onClick={() => (mode === 'nova' ? handleCreateWithNova() : void handleCreate())}
        >
          {isCreating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isUpdate ? 'Saving...' : mode === 'import' ? 'Uploading...' : 'Creating...'}
            </>
          ) : isUpdate ? (
            'Save'
          ) : mode === 'nova' ? (
            'Start chat'
          ) : mode === 'import' ? (
            'Import skill'
          ) : (
            'Add skill'
          )}
        </Button>
      </div>
    </div>
  );
}
